import {useState} from "react";
import {App as AntdApp, Card, Col, Row, Progress, Tag, Table, Alert, Tooltip, Select, Button as AntdButton} from "antd";
import dayjs from "dayjs";
import {PageHeader, Loading, FontAwesomeIcon} from "~/components";
import {rtkErrorMessage} from "~/reduxs/api/apiSlice";
import {
	useGetMySubscriptionQuery,
	useGetPlansQuery,
	useGetMyTokenLedgerQuery,
	useGetMyUpgradeRequestQuery,
	useRequestUpgradeMutation,
	useGetMyOrdersQuery,
	useConfirmUpgradePaidMutation,
	useCancelUpgradeRequestMutation,
	useScheduleDowngradeMutation,
	useCancelDowngradeMutation,
} from "~/reduxs/api/subscriptionApiSlice";
import UpgradeRequestModal from "../components/UpgradeRequestModal";
import PaymentModal from "../components/PaymentModal";
import ExtraPackSection from "../components/ExtraPackSection";
import style from "../style/Subscription.module.scss";
import className from "classnames/bind";

const cn = className.bind(style);

const nf = (n) => Number(n || 0).toLocaleString('vi-VN');

/** Màu thanh tiến trình theo mức đã dùng (xanh → cam → đỏ khi sắp hết). */
const barColor = (pct) => (pct >= 90 ? '#dc2626' : pct >= 70 ? '#f59e0b' : '#16a34a');

/** "còn X ngày" / "còn X giờ" tới mốc làm mới/hết hạn. */
const countdownLabel = (at) => {
	if (!at) return '';
	const target = dayjs(at);
	const now = dayjs();
	if (!target.isValid() || !target.isAfter(now)) return 'sắp làm mới';
	const days = target.diff(now, 'day');
	if (days >= 1) return `còn ${days} ngày`;
	const hours = target.diff(now, 'hour');
	if (hours >= 1) return `còn ${hours} giờ`;
	return 'trong hôm nay';
};

/** Gói được gợi ý (được đánh dấu "Phổ biến" trong lưới giá). */
const RECOMMENDED = 'pro';

/** Tagline dự phòng theo slug khi gói chưa có mô tả trong DB. */
const PLAN_TAGLINE = {
	free: 'Bắt đầu miễn phí',
	base: 'Cho cá nhân & freelancer',
	pro: 'Cho đội nhóm chuyên nghiệp',
	max: 'Cho agency & doanh nghiệp',
};

/** Các tính năng boolean hiển thị dạng checklist trong thẻ giá. */
const CARD_FEATURES = [
	{key: 'can_connect_gsc', label: 'Kết nối Google Search Console'},
	{key: 'can_ai_deep', label: 'Trợ lý AI chuyên sâu'},
	{key: 'can_keyword_tracking', label: 'Theo dõi từ khoá'},
	{key: 'can_keyword_gap', label: 'Content Gap từ khoá'},
	{key: 'can_export_pdf', label: 'Xuất báo cáo PDF'},
	{key: 'can_export_excel', label: 'Xuất báo cáo Excel'},
];

/** Nhãn tần suất crawl định kỳ của gói (từ limits.crawl_schedule_options). */
const SCHEDULE_FREQ_LABEL = {daily: 'Ngày', weekly: 'Tuần', monthly: 'Tháng'};
const scheduleOptionsLabel = (opts) => {
	if (!Array.isArray(opts) || opts.length === 0) return '—';
	return ['daily', 'weekly', 'monthly'].filter((v) => opts.includes(v)).map((v) => SCHEDULE_FREQ_LABEL[v]).join(', ');
};

/** Nhãn + màu trạng thái đơn hàng gói (lịch sử đơn của user). */
const ORDER_STATUS_TAG = {
	pending: {color: 'gold', label: 'Chờ thanh toán / duyệt'},
	approved: {color: 'green', label: 'Đã kích hoạt'},
	rejected: {color: 'red', label: 'Bị từ chối'},
	cancelled: {color: 'default', label: 'Đã huỷ'},
};

/** Nhãn + màu loại đơn hàng gói (kind). */
const ORDER_KIND_TAG = {
	upgrade: {color: 'purple', label: 'Nâng cấp'},
	renew: {color: 'blue', label: 'Gia hạn'},
	downgrade: {color: 'orange', label: 'Hạ gói'},
};

/** Nhãn + màu cho loại giao dịch token (khớp MemberDetail để nhất quán). */
const REASON_LABELS = {
	consume: 'Tiêu dùng AI',
	admin_add: 'Admin cộng',
	admin_deduct: 'Admin trừ',
	reset: 'Reset tháng',
	bonus: 'Token thưởng',
};
const REASON_COLOR = {
	consume: 'red',
	admin_add: 'green',
	admin_deduct: 'orange',
	reset: 'default',
	bonus: 'blue',
};

/** Cột bảng lịch sử token của user. */
const LEDGER_COLUMNS = [
	{
		title: 'Thời gian', dataIndex: 'created', key: 'created', width: 160, responsive: ['md'],
		render: (v) => (v ? dayjs(v).format('HH:mm DD/MM/YYYY') : '—'),
	},
	{
		title: 'Loại', dataIndex: 'reason', key: 'reason', width: 140,
		render: (v) => <Tag color={REASON_COLOR[v] || 'default'}>{REASON_LABELS[v] || v}</Tag>,
	},
	{title: 'Chi tiết', dataIndex: 'note', key: 'note', render: (v) => v || '—'},
	{
		title: 'Token', dataIndex: 'delta', key: 'delta', align: 'right', width: 120,
		render: (v) => <span style={{color: v >= 0 ? '#16a34a' : '#dc2626', fontWeight: 600}}>{v >= 0 ? '+' : ''}{nf(v)}</span>,
	},
];

/**
 * 1 đồng hồ hạn mức (token/URL/dự án) — số còn lại nổi bật + thanh tiến trình.
 * renew=true: hạn mức theo CHU KỲ GÓI (chỉ làm mới khi gia hạn — resetAt là hạn gia hạn,
 * null = gói vô thời hạn); false/không truyền: tự làm mới theo lịch như cũ.
 */
function UsageMeter({icon, label, used, limit, remaining, resetAt, unlimited, ownKey, hint, renew}) {
	const rem = remaining != null ? remaining : Math.max(0, (limit || 0) - (used || 0));
	const pct = unlimited || ownKey || !limit ? 0 : Math.min(100, Math.round(((used || 0) / limit) * 100));
	const special = unlimited || ownKey;

	return (
		<div className={cn('meter')}>
			<div className={cn('meter-top')}>
				<span className={cn('meter-icon')}><FontAwesomeIcon icon={icon} /></span>
				<span className={cn('meter-label')}>
					{label}
					{hint && (
						<Tooltip title={hint}>
							<FontAwesomeIcon icon="fa-light fa-circle-question" style={{marginLeft: 6, color: '#9ca3af', cursor: 'help'}} />
						</Tooltip>
					)}
				</span>
			</div>

			<div className={cn('meter-figure')}>
				{special ? (
					<span className={cn('meter-unlimited')}>{ownKey ? 'Khoá AI riêng' : 'Không giới hạn'}</span>
				) : (
					<>
						<span className={cn('meter-remaining')}>{nf(rem)}</span>
						<span className={cn('meter-of')}>còn lại / {nf(limit)}</span>
					</>
				)}
			</div>

			<Progress
				percent={special ? 100 : pct}
				showInfo={false}
				size="small"
				strokeColor={special ? '#7c3aed' : barColor(pct)}
				trailColor="#eef1f4"
			/>

			<div className={cn('meter-foot')}>
				{unlimited
					? 'Tài khoản quản trị'
					: ownKey
						? 'Dùng khoá AI của riêng bạn'
						: `Đã dùng ${nf(used)}${resetAt
						? (renew ? ' · làm mới khi gia hạn (hạn ' + dayjs(resetAt).format('DD/MM') + ')' : ' · tự làm mới ' + dayjs(resetAt).format('DD/MM'))
						: renew ? ' · làm mới khi gia hạn gói' : ''}`}
			</div>
		</div>
	);
}

/**
 * Gom mọi mốc tự làm mới / hết hạn hạn mức của user vào một chỗ.
 * Chỉ liệt kê hạn mức người dùng nhìn thấy (AI Token, URL kiểm tra, Bài viết AI)
 * + ngày hết hạn bài Gói Extra đã mua. Ẩn với tài khoản quản trị (không có hạn mức).
 */
function ResetSchedule({sub, ownKey, showContentMeter, contentExtra}) {
	// Gói trả phí: hạn mức theo CHU KỲ GÓI — chỉ làm mới khi gia hạn (reset_at = hạn gia hạn).
	const renew = sub.billing?.cycle === 'purchase';
	const monthlyFreq = renew ? 'Làm mới khi gia hạn gói' : 'Làm mới hằng tháng';

	const rows = [
		{
			icon: 'fa-light fa-wand-magic-sparkles',
			label: 'AI Token',
			freq: monthlyFreq,
			at: sub.ai_tokens?.reset_at,
			renew,
			show: !ownKey && (sub.ai_tokens?.limit || 0) > 0,
		},
		{
			icon: 'fa-light fa-link',
			label: 'URL kiểm tra',
			freq: 'Làm mới hằng tuần',
			at: sub.url_checks?.reset_at,
			show: (sub.url_checks?.limit || 0) > 0,
		},
		{
			icon: 'fa-light fa-feather-pointed',
			label: 'Bài viết AI tự động',
			freq: monthlyFreq,
			at: sub.auto_content_posts?.reset_at,
			renew,
			show: showContentMeter,
		},
		{
			icon: 'fa-light fa-hourglass-half',
			label: contentExtra > 0 ? `Bài Extra đã mua (${nf(contentExtra)} bài)` : 'Bài Extra đã mua',
			freq: renew ? 'Không cộng dồn sang chu kỳ sau' : 'Không cộng dồn sang tháng sau',
			at: sub.auto_content_posts?.reset_at,
			expiry: true,
			show: contentExtra > 0,
		},
	].filter((r) => r.show && r.at);

	if (rows.length === 0) return null;

	return (
		<Card
			className={cn('plans-card', 'reset-card')}
			title={<span><FontAwesomeIcon icon="fa-light fa-arrows-rotate" /> Mốc làm mới hạn mức</span>}
		>
			<div className={cn('reset-list')}>
				{rows.map((r, i) => (
					<div key={i} className={cn('reset-row', {expiry: r.expiry})}>
						<span className={cn('reset-icon')}><FontAwesomeIcon icon={r.icon} /></span>
						<div className={cn('reset-main')}>
							<div className={cn('reset-label')}>{r.label}</div>
							<div className={cn('reset-freq')}>{r.freq}</div>
						</div>
						<div className={cn('reset-when')}>
							<div className={cn('reset-date')}>
								{r.expiry ? 'Hết hạn ' : r.renew ? 'Gia hạn trước ' : 'Làm mới '}{dayjs(r.at).format('DD/MM/YYYY')}
							</div>
							<div className={cn('reset-count')}>{countdownLabel(r.at)}</div>
						</div>
					</div>
				))}
			</div>
			<div className={cn('reset-note')}>
				<FontAwesomeIcon icon="fa-light fa-circle-info" style={{marginRight: 6, color: '#9ca3af'}} />
				{renew
					? 'Hạn mức làm mới khi bạn gia hạn gói — hết hạn mà chưa gia hạn thì hạn mức KHÔNG tự về 0 và gói trở về Miễn phí. Bài Gói Extra hết chu kỳ là mất, không cộng dồn.'
					: 'Hạn mức tự về 0 khi sang kỳ mới — bạn không cần thao tác gì. Bài Gói Extra hết tháng là mất, không cộng dồn.'}
			</div>
		</Card>
	);
}

/**
 * 1 thẻ giá gói (pricing card) — quota nổi bật + checklist tính năng + CTA.
 * downgrade=true: gói rẻ hơn gói trả phí đang dùng → CTA "Hạ gói" (lên lịch, áp dụng khi
 * hết hạn); scheduledNext=true: gói này đã được lên lịch hạ xuống (huỷ ở banner đầu trang).
 */
function PlanCard({plan, current, recommended, pending, requestLocked, hideCta, downgrade, scheduledNext, scheduledAt, onUpgrade, onDowngrade}) {
	const L = plan.limits || {};

	return (
		<div className={cn('plan', {current, recommended})} data-plan={plan.slug}>
			{recommended && (
				<span className={cn('plan-badge')}>
					<FontAwesomeIcon icon="fa-solid fa-star" /> Phổ biến
				</span>
			)}

			<span className={cn('plan-name')}>{plan.name}</span>
			<div className={cn('plan-tag')}>{plan.description || PLAN_TAGLINE[plan.slug] || ''}</div>

			<div className={cn('plan-price')}>
				{plan.price > 0 ? (
					<>
						<span className={cn('plan-amount')}>{nf(plan.price)}đ</span>
						<span className={cn('plan-cycle')}>/tháng</span>
					</>
				) : (
					<span className={cn('plan-amount')}>Miễn phí</span>
				)}
				{/* Nâng cấp giữa chu kỳ: chỉ trả phí bù chênh lệch cho số ngày còn lại. */}
				{!current && plan.prorate && (
					<div className={cn('plan-tag')} style={{color: '#dc2626', fontWeight: 600}}>
						Nâng cấp hôm nay chỉ {nf(plan.prorate.amount)}đ
					</div>
				)}
			</div>

			<div className={cn('plan-quota')}>
				<div className={cn('quota-row')}>
					<FontAwesomeIcon icon="fa-light fa-wand-magic-sparkles" />
					<span><strong>{nf(L.ai_token_limit)}</strong> token AI / tháng</span>
				</div>
				<div className={cn('quota-row')}>
					<FontAwesomeIcon icon="fa-light fa-link" />
					<span><strong>{nf(L.url_checks_per_period)}</strong> URL kiểm tra / tuần</span>
				</div>
				<div className={cn('quota-row')}>
					<FontAwesomeIcon icon="fa-light fa-folder-tree" />
					<span><strong>{nf(L.max_projects)}</strong> dự án · {nf(L.max_urls_per_project)} URL/dự án</span>
				</div>
			</div>

			<ul className={cn('plan-feats')}>
				{CARD_FEATURES.map((f) => (
					<li key={f.key} className={cn('feat', {on: !!L[f.key]})}>
						<FontAwesomeIcon icon={L[f.key] ? 'fa-light fa-circle-check' : 'fa-light fa-circle-minus'} />
						<span>{f.label}</span>
					</li>
				))}
			</ul>

			<div className={cn('plan-cta')}>
				{/* Gói trả phí đang dùng: hạn mức chỉ làm mới khi GIA HẠN → cho gửi yêu cầu gia hạn chính gói này. */}
				{current && !(plan.price > 0) ? (
					<div className={cn('plan-btn', 'is-current')}>
						<FontAwesomeIcon icon="fa-light fa-circle-check" /> Đang sử dụng
					</div>
				) : hideCta ? null : scheduledNext ? (
					<Tooltip title="Huỷ quyết định hạ gói ở thông báo đầu trang.">
						<div className={cn('plan-btn', 'is-current')}>
							<FontAwesomeIcon icon="fa-light fa-calendar-check" /> Hạ gói {scheduledAt ? `từ ${dayjs(scheduledAt).format('DD/MM')}` : 'đã lên lịch'}
						</div>
					</Tooltip>
				) : pending ? (
					<div className={cn('plan-btn', 'is-current')}>
						<FontAwesomeIcon icon="fa-light fa-hourglass-half" /> Đang chờ duyệt
					</div>
				) : requestLocked ? (
					<Tooltip title="Bạn đã có một yêu cầu đang chờ duyệt. Vui lòng đợi quản trị viên xử lý.">
						<div className={cn('plan-btn')}>{current ? 'Gia hạn gói' : downgrade ? 'Hạ gói' : 'Yêu cầu nâng cấp'}</div>
					</Tooltip>
				) : downgrade ? (
					<div
						className={cn('plan-btn')}
						role="button"
						tabIndex={0}
						onClick={() => onDowngrade && onDowngrade(plan)}
						onKeyDown={(e) => e.key === 'Enter' && onDowngrade && onDowngrade(plan)}
					>
						Hạ gói
					</div>
				) : (
					<div
						className={cn('plan-btn')}
						role="button"
						tabIndex={0}
						onClick={() => onUpgrade && onUpgrade(plan)}
						onKeyDown={(e) => e.key === 'Enter' && onUpgrade && onUpgrade(plan)}
					>
						{current ? 'Gia hạn gói' : 'Yêu cầu nâng cấp'}
					</div>
				)}
			</div>
		</div>
	);
}

function SubscriptionPage() {
	const {notification} = AntdApp.useApp();
	const {data: sub, isLoading, isError} = useGetMySubscriptionQuery();
	const {data: plans = []} = useGetPlansQuery();
	const {data: ledger = []} = useGetMyTokenLedgerQuery();
	const {data: upgradeReq} = useGetMyUpgradeRequestQuery();
	const {data: orders = []} = useGetMyOrdersQuery();
	const [requestUpgrade, {isLoading: sendingRequest}] = useRequestUpgradeMutation();
	const [confirmPaid, {isLoading: confirmingPaid}] = useConfirmUpgradePaidMutation();
	const [cancelOrder, {isLoading: cancellingOrder}] = useCancelUpgradeRequestMutation();
	const [scheduleDowngrade, {isLoading: schedulingDowngrade}] = useScheduleDowngradeMutation();
	const [cancelDowngrade, {isLoading: cancellingDowngrade}] = useCancelDowngradeMutation();

	// Lọc lịch sử token theo loại giao dịch (client-side — BE trả tối đa 100 dòng).
	const [reasonFilter, setReasonFilter] = useState('all');
	const filteredLedger = reasonFilter === 'all' ? ledger : ledger.filter((r) => r.reason === reasonFilter);

	// Gói đang chọn để gửi yêu cầu nâng cấp/hạ gói (null = đóng modal).
	const [upgradePlan, setUpgradePlan] = useState(null);
	// Mở màn thanh toán VietQR cho đơn pending (true khi user vừa tạo đơn / bấm "Xem thanh toán").
	const [showPayment, setShowPayment] = useState(false);

	// Khách chỉ bấm xác nhận (không nhập lời nhắn) → tạo đơn hàng → sang màn thanh toán QR.
	const handleSendRequest = async () => {
		try {
			const res = await requestUpgrade({plan_slug: upgradePlan.slug}).unwrap();
			notification.success({message: 'Đã tạo đơn hàng', description: res?.message || 'Thanh toán rồi bấm "Tôi đã thanh toán" để quản trị viên xác nhận.'});
			setUpgradePlan(null);
			setShowPayment(true); // sang bước 2: quét QR thanh toán (đơn đọc từ upgradeReq refetch)
		} catch (e) {
			notification.error({message: 'Lỗi', description: rtkErrorMessage(e, 'Không gửi được yêu cầu.')});
		}
	};

	// Hạ gói = LÊN LỊCH (không tạo đơn): gói hiện tại dùng tiếp tới hết hạn rồi mới chuyển.
	const handleScheduleDowngrade = async () => {
		try {
			const res = await scheduleDowngrade({plan_slug: upgradePlan.slug}).unwrap();
			notification.success({message: 'Đã lên lịch hạ gói', description: res?.message});
			setUpgradePlan(null);
		} catch (e) {
			notification.error({message: 'Lỗi', description: rtkErrorMessage(e, 'Không lên lịch được — thử lại.')});
		}
	};

	const handleCancelDowngrade = async () => {
		try {
			const res = await cancelDowngrade().unwrap();
			notification.success({message: res?.message || 'Đã huỷ quyết định hạ gói.'});
		} catch (e) {
			notification.error({message: 'Lỗi', description: rtkErrorMessage(e, 'Không huỷ được — thử lại.')});
		}
	};

	const handleConfirmPaid = async () => {
		try {
			const res = await confirmPaid().unwrap();
			notification.success({message: 'Đã ghi nhận', description: res?.message || 'Quản trị viên sẽ đối soát và kích hoạt gói cho bạn.'});
		} catch (e) {
			notification.error({message: 'Lỗi', description: rtkErrorMessage(e, 'Không ghi nhận được — thử lại.')});
		}
	};

	const handleCancelOrder = async () => {
		try {
			const res = await cancelOrder().unwrap();
			notification.success({message: res?.message || 'Đã huỷ đơn hàng.'});
			setShowPayment(false);
		} catch (e) {
			notification.error({message: 'Lỗi', description: rtkErrorMessage(e, 'Không huỷ được đơn.')});
		}
	};

	if (isLoading) return <Loading />;

	if (isError || !sub) {
		return (
			<div className="container">
				<Alert type="error" message="Không tải được thông tin gói. Vui lòng thử lại." showIcon />
			</div>
		);
	}

	const isRoot = Boolean(sub.is_root);
	const ownKey = Boolean(sub.require_own_ai_key);
	const currentSlug = sub.plan?.slug;

	// Meter thứ 4 "Bài viết AI (tháng)" — chỉ hiện khi gói có quota (limit đã gồm bài Extra).
	const showContentMeter = (sub.auto_content_posts?.limit || 0) > 0;
	const meterCol = showContentMeter ? {xs: 24, md: 12, xl: 6} : {xs: 24, md: 8};
	const contentExtra = sub.auto_content_posts?.extra || 0;
	// Gói trả phí: hạn mức theo chu kỳ mua — chỉ làm mới khi gia hạn (BE trả billing.cycle).
	const renewCycle = sub.billing?.cycle === 'purchase';
	const sortedPlans = [...plans].sort((a, b) => (a.price || 0) - (b.price || 0));
	// Lịch hạ gói đang chờ (áp dụng khi gói hiện tại hết hạn — huỷ được trước đó).
	const nextPlan = sub.billing?.next_plan || null;
	// Cho phép lên lịch hạ gói khi đang dùng gói trả phí còn hạn (có mốc hết hạn để "chờ").
	const canDowngrade = !isRoot && renewCycle && Boolean(sub.billing?.expires) && (sub.plan?.price || 0) > 0;
	// Modal đang mở ở chế độ hạ gói? (gói chọn rẻ hơn gói hiện tại, không phải gia hạn)
	const modalDowngrade = Boolean(upgradePlan) && canDowngrade
		&& upgradePlan.slug !== currentSlug && (upgradePlan.price || 0) < (sub.plan?.price || 0);

	// Các hàng bảng so sánh chi tiết (đọc từ limits của từng gói).
	const boolCell = (v) =>
		v ? (
			<FontAwesomeIcon icon="fa-light fa-circle-check" className={cn('cell-yes')} />
		) : (
			<FontAwesomeIcon icon="fa-light fa-minus" className={cn('cell-no')} />
		);

	const rows = [
		{key: 'price', label: 'Giá / tháng', render: (p) => (p.price > 0 ? `${nf(p.price)}đ` : 'Miễn phí')},
		{key: 'ai_token_limit', label: 'AI Token / tháng', render: (p) => nf(p.limits.ai_token_limit)},
		{key: 'url_checks_per_period', label: 'URL kiểm tra / tuần', render: (p) => nf(p.limits.url_checks_per_period)},
		{key: 'max_projects', label: 'Số dự án', render: (p) => nf(p.limits.max_projects)},
		{key: 'max_urls_per_project', label: 'URL tối đa / dự án', render: (p) => nf(p.limits.max_urls_per_project)},
		{key: 'crawl_min_interval_min', label: 'Tần suất crawl', render: (p) => `${p.limits.crawl_min_interval_min}'`},
		{key: 'crawl_schedule_options', label: 'Crawl định kỳ', render: (p) => scheduleOptionsLabel(p.limits.crawl_schedule_options)},
		{key: 'history_retention_days', label: 'Lưu lịch sử', render: (p) => (p.limits.history_retention_days > 0 ? `${p.limits.history_retention_days} ngày` : '∞')},
		{key: 'can_connect_gsc', label: 'Kết nối GSC', render: (p) => boolCell(p.limits.can_connect_gsc)},
		{key: 'can_ai_deep', label: 'AI chuyên sâu', render: (p) => boolCell(p.limits.can_ai_deep)},
		{key: 'can_export_pdf', label: 'Xuất PDF', render: (p) => boolCell(p.limits.can_export_pdf)},
		{key: 'can_export_excel', label: 'Xuất Excel', render: (p) => boolCell(p.limits.can_export_excel)},
		{key: 'can_keyword_tracking', label: 'Theo dõi từ khoá', render: (p) => boolCell(p.limits.can_keyword_tracking)},
		{key: 'can_keyword_gap', label: 'Content Gap từ khoá', render: (p) => boolCell(p.limits.can_keyword_gap)},
		{key: 'max_keyword_items', label: 'Kho từ khoá / dự án', render: (p) => nf(p.limits.max_keyword_items)},
		{key: 'keyword_lookups_per_month', label: 'Tra volume/CPC/KD / tháng', render: (p) => ((p.limits.keyword_lookups_per_month || 0) > 0 ? nf(p.limits.keyword_lookups_per_month) : boolCell(false))},
		{key: 'keyword_lookups_per_day', label: 'Tra volume/CPC/KD / ngày', render: (p) => ((p.limits.keyword_lookups_per_month || 0) <= 0 ? boolCell(false) : ((p.limits.keyword_lookups_per_day || 0) > 0 ? nf(p.limits.keyword_lookups_per_day) : 'Không giới hạn'))},
		{key: 'serp_checks_per_month', label: 'Theo dõi thứ hạng SERP / tháng', render: (p) => ((p.limits.serp_checks_per_month || 0) > 0 ? nf(p.limits.serp_checks_per_month) : boolCell(false))},
		{key: 'backlink_syncs_per_month', label: 'Đồng bộ backlink / tháng', render: (p) => ((p.limits.backlink_syncs_per_month || 0) > 0 ? nf(p.limits.backlink_syncs_per_month) : boolCell(false))},
		{key: 'competitor_lookups_per_month', label: 'Phân tích sâu đối thủ / tháng', render: (p) => ((p.limits.competitor_lookups_per_month || 0) > 0 ? nf(p.limits.competitor_lookups_per_month) : boolCell(false))},
		{key: 'auto_content_posts_per_month', label: 'Bài viết AI tự động / tháng', render: (p) => (p.limits.can_auto_content && (p.limits.auto_content_posts_per_month || 0) > 0 ? nf(p.limits.auto_content_posts_per_month) : boolCell(false))},
	];

	const columns = [
		{title: 'Tính năng', dataIndex: 'label', key: 'label', fixed: 'left', width: 190},
		...sortedPlans.map((p) => ({
			title: (
				<div className={cn('plan-col', {current: p.slug === currentSlug})}>
					<span className={cn('plan-col-name')}>{p.name}</span>
					{p.slug === currentSlug && <Tag color="red" className={cn('plan-current-tag')}>Đang dùng</Tag>}
				</div>
			),
			key: p.slug,
			align: 'center',
			onCell: () => ({className: p.slug === currentSlug ? cn('col-current') : undefined}),
			render: (_, row) => row.render(p),
		})),
	];

	// Cột bảng lịch sử đơn hàng gói. Đơn pending → nút mở lại màn thanh toán QR.
	const orderColumns = [
		{
			title: 'Thời gian', dataIndex: 'created', key: 'created', width: 150, responsive: ['md'],
			render: (v) => (v ? dayjs(v).format('HH:mm DD/MM/YYYY') : '—'),
		},
		{
			title: 'Gói', key: 'plan',
			render: (_, r) => {
				const k = ORDER_KIND_TAG[r.kind] || ORDER_KIND_TAG.upgrade;
				return (
					<span>
						{r.plan_name || r.plan_slug}{' '}
						<Tag color={k.color}>{k.label}</Tag>
					</span>
				);
			},
		},
		{
			title: 'Số tiền', dataIndex: 'amount', key: 'amount', align: 'right', width: 110,
			render: (v) => (v > 0 ? `${nf(v)}đ` : '—'),
		},
		{
			title: 'Mã đơn', dataIndex: 'code', key: 'code', width: 130, responsive: ['md'],
			render: (v) => (v ? <code>{v}</code> : '—'),
		},
		{
			title: 'Trạng thái', dataIndex: 'status', key: 'status', width: 170,
			render: (v, r) => {
				const m = ORDER_STATUS_TAG[v] || {color: 'default', label: v};
				return (
					<span>
						<Tag color={m.color}>{m.label}</Tag>
						{v === 'pending' && r.paid_at && <Tag color="green">Đã báo CK</Tag>}
					</span>
				);
			},
		},
		{
			title: '', key: 'action', align: 'right', width: 130,
			render: (_, r) => (r.status === 'pending' ? (
				<AntdButton size="small" type="primary" onClick={() => setShowPayment(true)}>
					{r.paid_at ? 'Xem đơn' : 'Thanh toán'}
				</AntdButton>
			) : null),
		},
	];

	return (
		<div className="container">
			<PageHeader
				icon="fa-light fa-crown"
				title="Gói & Hạn mức"
				subtitle="Theo dõi gói hiện tại, hạn mức token AI, URL kiểm tra và số dự án của bạn."
			/>

			{/* Hero — gói hiện tại */}
			{/* Đơn hàng đang chờ thanh toán/duyệt (đăng ký/nâng cấp/gia hạn — kể cả đơn gia hạn
			    do hệ thống tự tạo trước 3 ngày hết hạn) — banner + nút mở màn thanh toán QR. */}
			{!isRoot && upgradeReq?.status === 'pending' && (
				<Alert
					type={upgradeReq.kind === 'renew' || upgradeReq.kind === 'downgrade' ? 'warning' : 'info'}
					showIcon
					style={{marginBottom: 16}}
					message={upgradeReq.kind === 'renew'
						? `Đơn gia hạn gói đang chờ thanh toán — ${nf(upgradeReq.amount)}đ (mã ${upgradeReq.code || '—'})`
						: upgradeReq.kind === 'downgrade'
							? `Đơn chuyển sang gói "${plans.find((p) => p.slug === upgradeReq.plan_slug)?.name || upgradeReq.plan_slug}" (hạ gói theo lịch) đang chờ thanh toán — ${nf(upgradeReq.amount)}đ (mã ${upgradeReq.code || '—'})`
							: `Đơn đăng ký gói "${plans.find((p) => p.slug === upgradeReq.plan_slug)?.name || upgradeReq.plan_slug}" đang chờ — ${nf(upgradeReq.amount)}đ (mã ${upgradeReq.code || '—'})`}
					description={upgradeReq.paid_at
						? 'Bạn đã báo thanh toán — quản trị viên sẽ đối soát và kích hoạt trong thời gian sớm nhất.'
						: 'Quét QR chuyển khoản rồi bấm "Tôi đã thanh toán" để quản trị viên xác nhận.'}
					action={
						<AntdButton size="small" type="primary" onClick={() => setShowPayment(true)}>
							{upgradeReq.paid_at ? 'Xem đơn hàng' : 'Thanh toán ngay'}
						</AntdButton>
					}
				/>
			)}

			{/* Lịch hạ gói đang chờ — gói hiện tại dùng tiếp tới hết hạn rồi mới chuyển; huỷ được. */}
			{!isRoot && nextPlan && (
				<Alert
					type="warning"
					showIcon
					style={{marginBottom: 16}}
					message={`Đã lên lịch hạ xuống gói "${nextPlan.name}"${nextPlan.effective_at ? ` — áp dụng từ ${dayjs(nextPlan.effective_at).format('DD/MM/YYYY')}` : ''}`}
					description="Bạn vẫn dùng đầy đủ gói hiện tại đến hết hạn. Đổi ý? Huỷ quyết định hạ gói bất cứ lúc nào trước mốc trên."
					action={
						<AntdButton size="small" danger loading={cancellingDowngrade} onClick={handleCancelDowngrade}>
							Huỷ hạ gói
						</AntdButton>
					}
				/>
			)}

			<div className={cn('hero')} data-plan={currentSlug}>
				<div className={cn('hero-left')}>
					<span className={cn('hero-eyebrow')}>
						<FontAwesomeIcon icon="fa-light fa-crown" /> {isRoot ? 'Tài khoản quản trị' : 'Gói hiện tại'}
					</span>
					<div className={cn('hero-name')}>{sub.plan?.name}</div>
					<div className={cn('hero-price')}>
						{isRoot
							? 'Toàn quyền · không giới hạn hạn mức'
							: sub.plan?.price > 0
								? `${nf(sub.plan.price)}đ / tháng`
								: 'Miễn phí'}
					</div>
				</div>
				<div className={cn('hero-right')}>
					{isRoot ? (
						<span className={cn('hero-remaining')}><FontAwesomeIcon icon="fa-light fa-infinity" /></span>
					) : ownKey ? (
						<>
							<span className={cn('hero-remaining')}><FontAwesomeIcon icon="fa-light fa-key" /></span>
							<span className={cn('hero-remaining-label')}>Dùng khoá AI riêng của bạn</span>
						</>
					) : (
						<>
							<span className={cn('hero-remaining')}>{nf(sub.ai_tokens?.remaining)}</span>
							<span className={cn('hero-remaining-label')}>{renewCycle ? 'token AI còn lại trong chu kỳ' : 'token AI còn lại tháng này'}</span>
						</>
					)}
				</div>
			</div>

			{/* Đồng hồ hạn mức */}
			<Row gutter={[16, 16]} className={cn('meters')}>
				<Col {...meterCol}>
					<UsageMeter
						icon="fa-light fa-wand-magic-sparkles"
						label={renewCycle ? 'AI Token (chu kỳ gói)' : 'AI Token (tháng)'}
						hint={renewCycle
							? 'Hạn mức đã bao gồm token thưởng do quản trị viên cộng thêm (nếu có). Làm mới khi gia hạn gói — không tự reset theo lịch.'
							: 'Hạn mức tháng đã bao gồm token thưởng do quản trị viên cộng thêm (nếu có). Tự làm mới đầu mỗi tháng.'}
						used={sub.ai_tokens?.used}
						limit={sub.ai_tokens?.limit}
						remaining={sub.ai_tokens?.remaining}
						resetAt={sub.ai_tokens?.reset_at}
						unlimited={isRoot}
						ownKey={ownKey}
						renew={renewCycle}
					/>
				</Col>
				<Col {...meterCol}>
					<UsageMeter
						icon="fa-light fa-link"
						label="URL kiểm tra (tuần)"
						used={sub.url_checks?.used}
						limit={sub.url_checks?.limit}
						remaining={sub.url_checks?.remaining}
						resetAt={sub.url_checks?.reset_at}
						unlimited={isRoot}
					/>
				</Col>
				<Col {...meterCol}>
					<UsageMeter
						icon="fa-light fa-folder-tree"
						label="Dự án"
						used={sub.projects?.used}
						limit={sub.projects?.limit}
						unlimited={isRoot}
					/>
				</Col>
				{showContentMeter && (
					<Col {...meterCol}>
						<UsageMeter
							icon="fa-light fa-feather-pointed"
							label={renewCycle ? 'Bài viết AI (chu kỳ gói)' : 'Bài viết AI (tháng)'}
							hint={contentExtra > 0
								? `Đã gồm ${contentExtra} bài từ Gói Extra — hết hạn cuối ${renewCycle ? 'chu kỳ gói' : 'tháng'}.`
								: undefined}
							used={sub.auto_content_posts?.used}
							limit={sub.auto_content_posts?.limit}
							remaining={sub.auto_content_posts?.remaining}
							resetAt={sub.auto_content_posts?.reset_at}
							unlimited={isRoot}
							renew={renewCycle}
						/>
					</Col>
				)}
			</Row>

			{/* Mốc làm mới hạn mức — gom mọi thời điểm reset/hết hạn vào một chỗ */}
			{!isRoot && (
				<ResetSchedule
					sub={sub}
					ownKey={ownKey}
					showContentMeter={showContentMeter}
					contentExtra={contentExtra}
				/>
			)}

			{/* Lưới thẻ giá */}
			{sortedPlans.length > 0 && (
				<div className={cn('section')}>
					<div className={cn('section-head')}>
						<h3 className={cn('section-title')}>Các gói dịch vụ</h3>
						<p className={cn('section-sub')}>Chọn gói phù hợp với quy mô SEO của bạn — nâng cấp bất cứ lúc nào.</p>
					</div>

					<Row gutter={[18, 18]}>
						{sortedPlans.map((p) => (
							<Col xs={24} sm={12} xl={6} key={p.slug}>
								<PlanCard
									plan={p}
									current={p.slug === currentSlug}
									recommended={p.slug === RECOMMENDED}
									hideCta={isRoot}
									pending={upgradeReq?.status === 'pending' && upgradeReq.plan_slug === p.slug}
									requestLocked={upgradeReq?.status === 'pending' && upgradeReq.plan_slug !== p.slug}
									downgrade={canDowngrade && p.slug !== currentSlug && (p.price || 0) < (sub.plan?.price || 0)}
									scheduledNext={nextPlan?.slug === p.slug}
									scheduledAt={nextPlan?.effective_at}
									onUpgrade={setUpgradePlan}
									onDowngrade={setUpgradePlan}
								/>
							</Col>
						))}
					</Row>
				</div>
			)}

			{/* Gói Extra — mua thêm bài viết AI (ẩn khi chưa có pack hoặc root) */}
			<ExtraPackSection sub={sub} />

			{/* Bảng so sánh chi tiết */}
			{sortedPlans.length > 0 && (
				<Card
					className={cn('plans-card')}
					title={<span><FontAwesomeIcon icon="fa-light fa-table-list" /> Bảng so sánh chi tiết</span>}
				>
					<Table
						className={cn('compare-table')}
						columns={columns}
						dataSource={rows.map((r) => ({...r}))}
						rowKey="key"
						pagination={false}
						size="middle"
						scroll={{x: 'max-content'}}
					/>
					<div className={cn('plans-note')}>
						<FontAwesomeIcon icon="fa-light fa-circle-info" /> Muốn đổi gói? Bấm <strong>Yêu cầu nâng cấp</strong> trên
						thẻ giá — quản trị viên sẽ liên hệ xác nhận và kích hoạt cho bạn.
					</div>
				</Card>
			)}

			{/* Lịch sử đơn hàng gói của chính user (đăng ký/nâng cấp/gia hạn — mọi trạng thái) */}
			{!isRoot && orders.length > 0 && (
				<Card
					className={cn('plans-card')}
					title={<span><FontAwesomeIcon icon="fa-light fa-receipt" /> Lịch sử đơn hàng</span>}
					extra={<span className={cn('ledger-hint')}>50 đơn gần nhất</span>}
				>
					<Table
						className={cn('compare-table')}
						columns={orderColumns}
						dataSource={orders}
						rowKey="id"
						size="small"
						scroll={{x: 'max-content'}}
						pagination={{pageSize: 10, hideOnSinglePage: true}}
					/>
				</Card>
			)}

			{/* Lịch sử token của chính user */}
			<Card
				className={cn('plans-card', 'ledger-card')}
				title={<span><FontAwesomeIcon icon="fa-light fa-clock-rotate-left" /> Lịch sử token</span>}
				extra={
					<span style={{display: 'inline-flex', alignItems: 'center', gap: 10}}>
						<Select
							size="small"
							value={reasonFilter}
							onChange={setReasonFilter}
							style={{minWidth: 150}}
							options={[
								{value: 'all', label: 'Tất cả loại'},
								...Object.entries(REASON_LABELS).map(([value, label]) => ({value, label})),
							]}
						/>
						<span className={cn('ledger-hint')}>100 giao dịch gần nhất</span>
					</span>
				}
			>
				<Table
					className={cn('compare-table')}
					columns={LEDGER_COLUMNS}
					dataSource={filteredLedger}
					rowKey="id"
					size="small"
					scroll={{x: 'max-content'}}
					pagination={{pageSize: 10, hideOnSinglePage: true}}
					locale={{emptyText: reasonFilter !== 'all'
						? 'Không có giao dịch nào thuộc loại này.'
						: 'Chưa có giao dịch token nào. Hãy dùng thử Trợ lý AI hoặc Sinh nội dung.'}}
				/>
			</Card>

			<UpgradeRequestModal
				open={Boolean(upgradePlan)}
				plan={upgradePlan}
				renew={upgradePlan?.slug === currentSlug}
				downgrade={modalDowngrade}
				effectiveAt={sub.billing?.expires}
				submitting={modalDowngrade ? schedulingDowngrade : sendingRequest}
				onCancel={() => setUpgradePlan(null)}
				onSubmit={modalDowngrade ? handleScheduleDowngrade : handleSendRequest}
			/>

			{/* Bước 2 — màn thanh toán VietQR của đơn pending (đơn luôn đọc từ upgradeReq nên
			    tự cập nhật sau khi bấm "Tôi đã thanh toán" / admin xử lý). */}
			<PaymentModal
				open={showPayment && upgradeReq?.status === 'pending'}
				order={upgradeReq?.status === 'pending' ? upgradeReq : null}
				planName={plans.find((p) => p.slug === upgradeReq?.plan_slug)?.name}
				confirming={confirmingPaid}
				cancelling={cancellingOrder}
				onConfirmPaid={handleConfirmPaid}
				onCancelOrder={handleCancelOrder}
				onClose={() => setShowPayment(false)}
			/>
		</div>
	);
}

export default SubscriptionPage;
